import { ColumnDef } from '@tanstack/react-table';
import cn from 'classnames';
import { ScormScoRow, ScormDropOffItem } from '@/entities/Dashboard';
import { formatDurationOrDash, formatRateOrDash, formatScoreOrDash } from '../lib/formatDuration';

const cellClass = 'text-base font-normal leading-[140%] text-black-headline';

export type ScormScoTableRow = ScormScoRow & { id: string };
export type ScormDropOffTableRow = ScormDropOffItem & { id: string };

export const scormScoColumns: ColumnDef<ScormScoTableRow>[] = [
  {
    header: 'Learning block',
    accessorKey: 'scoIdentifier',
    size: 200,
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className={cellClass}>{row.original.scoIdentifier || '—'}</span>
        <span className="text-sm text-grey-secondary">{row.original.packageTitle}</span>
      </div>
    ),
  },
  {
    header: 'Learners',
    accessorKey: 'learnersCount',
    size: 100,
    cell: ({ row }) => <span className={cellClass}>{row.original.learnersCount}</span>,
  },
  {
    header: 'Attempts',
    accessorKey: 'attemptsCount',
    size: 100,
    cell: ({ row }) => <span className={cellClass}>{row.original.attemptsCount}</span>,
  },
  {
    header: 'Completed',
    accessorKey: 'completedCount',
    size: 105,
    cell: ({ row }) => <span className={cellClass}>{row.original.completedCount}</span>,
  },
  {
    header: 'Completion',
    accessorKey: 'completionRate',
    size: 110,
    cell: ({ row }) => <span className={cellClass}>{formatRateOrDash(row.original.completionRate)}</span>,
  },
  {
    header: 'Avg score',
    accessorKey: 'avgScore',
    size: 100,
    cell: ({ row }) => <span className={cellClass}>{formatScoreOrDash(row.original.avgScore)}</span>,
  },
  {
    header: 'Avg time',
    accessorKey: 'avgTimeSpentSeconds',
    size: 100,
    cell: ({ row }) => (
      <span className={cellClass}>{formatDurationOrDash(row.original.avgTimeSpentSeconds)}</span>
    ),
  },
];

export const scormDropOffColumns: ColumnDef<ScormDropOffTableRow>[] = [
  {
    header: '#',
    accessorKey: 'orderIndex',
    size: 60,
    cell: ({ row }) => <span className={cellClass}>{row.original.orderIndex}</span>,
  },
  {
    header: 'Learning block',
    accessorKey: 'title',
    size: 220,
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className={cellClass}>{row.original.title || row.original.scoIdentifier || '—'}</span>
        {row.original.isOutsideManifest && (
          <span className="text-sm font-medium text-yellow-700">Not in manifest</span>
        )}
      </div>
    ),
  },
  {
    header: 'Reached',
    accessorKey: 'reachedCount',
    size: 95,
    cell: ({ row }) => <span className={cellClass}>{row.original.reachedCount}</span>,
  },
  {
    header: 'Stopped here',
    accessorKey: 'stoppedHereCount',
    size: 115,
    cell: ({ row }) => (
      <span className={cn(cellClass, row.original.stoppedHereCount > 0 && 'font-medium text-red-600')}>
        {row.original.stoppedHereCount}
      </span>
    ),
  },
  {
    header: 'Went further',
    accessorKey: 'passedThroughCount',
    size: 115,
    cell: ({ row }) => <span className={cellClass}>{row.original.passedThroughCount}</span>,
  },
  {
    header: 'Drop rate',
    accessorKey: 'dropRate',
    size: 100,
    cell: ({ row }) => (
      <span className={cn(cellClass, row.original.dropRate >= 50 && 'font-medium text-red-600')}>
        {formatRateOrDash(row.original.dropRate)}
      </span>
    ),
  },
  {
    header: 'Avg score',
    accessorKey: 'avgScore',
    size: 100,
    cell: ({ row }) => <span className={cellClass}>{formatScoreOrDash(row.original.avgScore)}</span>,
  },
  {
    header: 'Avg time',
    accessorKey: 'avgTimeSpentSeconds',
    size: 100,
    cell: ({ row }) => (
      <span className={cellClass}>{formatDurationOrDash(row.original.avgTimeSpentSeconds)}</span>
    ),
  },
];
